/**
 * E024-S001-T005 / E024-S002-T006: File Drop Choice Dialog
 *
 * When dropping importable files on edgeless canvas, the user can choose
 * between adding the file as an attachment or routing it through the matching
 * import flow.
 */

import { Modal } from '@affine/component';
import type {
  DialogComponentProps,
  WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import {
  AttachmentIcon,
  DatabaseTableViewIcon,
  ImageIcon,
  PageIcon,
} from '@blocksuite/icons/rc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useCallback } from 'react';

import {
  type EdgelessImportChoice,
  getEdgelessImportChoices,
  getEdgelessImportFileLabel,
} from '../../../modules/streamlain/file-import/edgeless-import-classifier';
import * as styles from './styles.css';

export const FileDropChoiceDialog = ({
  close,
  files,
  fileType,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['file-drop-choice']>) => {
  const t = useI18n();

  const handleChoice = useCallback(
    (choice: EdgelessImportChoice) => {
      close({ choice, files });
    },
    [close, files]
  );

  const handleCancel = useCallback(() => {
    close(undefined);
  }, [close]);

  // Get file info for display
  const fileCount = files?.length ?? 0;
  const fileName = fileCount === 1 ? files?.[0]?.name : `${fileCount} files`;
  const fileTypeLabel = getEdgelessImportFileLabel(fileType);
  const choices = getEdgelessImportChoices(fileType);

  const getChoiceContent = (choice: EdgelessImportChoice) => {
    switch (choice) {
      case 'attachment':
        return {
          title:
            t['com.streamlain.file-drop-choice.attachment.title']?.() ??
            'Add as Attachment',
          description:
            t['com.streamlain.file-drop-choice.attachment.description']?.() ??
            'Add the file to the canvas as a downloadable attachment',
          icon: <AttachmentIcon width={32} height={32} />,
        };
      case 'database':
        return {
          title:
            t['com.streamlain.file-drop-choice.database.title']?.() ??
            'Import as Database',
          description:
            t['com.streamlain.file-drop-choice.database.description']?.() ??
            'Convert the data into an editable database table',
          icon: <DatabaseTableViewIcon width={32} height={32} />,
        };
      case 'pages':
        return {
          title:
            t['com.streamlain.file-drop-choice.pages.title']?.() ??
            'Import as Page Images',
          description:
            t['com.streamlain.file-drop-choice.pages.description']?.() ??
            'Select pages and place them as images on the canvas',
          icon: <ImageIcon width={32} height={32} />,
        };
      case 'document':
        return {
          title:
            t['com.streamlain.file-drop-choice.document.title']?.() ??
            'Import as Document',
          description:
            t['com.streamlain.file-drop-choice.document.description']?.() ??
            'Convert the file into an editable document',
          icon: <PageIcon width={32} height={32} />,
        };
      case 'notes':
        return {
          title:
            t['com.streamlain.file-drop-choice.notes.title']?.() ??
            'Import as Notes',
          description:
            t['com.streamlain.file-drop-choice.notes.description']?.() ??
            'Convert each file into an editable note on the canvas',
          icon: <PageIcon width={32} height={32} />,
        };
      case 'html':
        return {
          title:
            t['com.streamlain.file-drop-choice.html.title']?.() ??
            'Import HTML',
          description:
            t['com.streamlain.file-drop-choice.html.description']?.() ??
            `Convert the HTML file into editable ${BRAND_DISPLAY_NAME} pages`,
          icon: <PageIcon width={32} height={32} />,
        };
      case 'markdownZip':
        return {
          title:
            t['com.streamlain.file-drop-choice.markdown-zip.title']?.() ??
            'Markdown with Media',
          description:
            t['com.streamlain.file-drop-choice.markdown-zip.description']?.() ??
            'Import a Markdown export zip with referenced media',
          icon: <PageIcon width={32} height={32} />,
        };
      case 'notionEnhanced':
        return {
          title:
            t['com.streamlain.file-drop-choice.notion.title']?.() ??
            'Notion Export',
          description:
            t['com.streamlain.file-drop-choice.notion.description']?.() ??
            'Open the Notion import preview for this zip',
          icon: <PageIcon width={32} height={32} />,
        };
      case 'snapshot':
        return {
          title:
            t['com.streamlain.file-drop-choice.snapshot.title']?.() ??
            'Snapshot',
          description:
            t['com.streamlain.file-drop-choice.snapshot.description']?.() ??
            `Import the zip as a ${BRAND_DISPLAY_NAME} snapshot`,
          icon: <PageIcon width={32} height={32} />,
        };
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open: boolean) => {
        if (!open) handleCancel();
      }}
      width={480}
      contentOptions={{
        style: {
          padding: '24px',
          background: cssVarV2('layer/background/primary'),
        },
      }}
    >
      <div className={styles.container}>
        <div className={styles.title}>
          {t['com.streamlain.file-drop-choice.title']?.() ??
            'How would you like to add this file?'}
        </div>
        <div className={styles.subtitle}>
          {fileName} ({fileTypeLabel})
        </div>

        <div className={styles.optionsContainer}>
          {choices.map(choice => {
            const content = getChoiceContent(choice);
            return (
              <button
                key={choice}
                className={styles.optionCard}
                onClick={() => handleChoice(choice)}
                data-testid={`file-drop-choice-${choice}`}
              >
                <div className={styles.optionIcon}>{content.icon}</div>
                <div className={styles.optionTitle}>{content.title}</div>
                <div className={styles.optionDescription}>
                  {content.description}
                </div>
              </button>
            );
          })}
        </div>

        <button className={styles.cancelButton} onClick={handleCancel}>
          {t['Cancel']?.() ?? 'Cancel'}
        </button>
      </div>
    </Modal>
  );
};
